import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';
import { GameOutput, LeagueOutput, DefaultService } from '../generated-api';

interface DataState {
  // Games
  games: GameOutput[];
  gamesLoading: boolean;
  gamesError: string | null;
  fetchGames: (force?: boolean) => Promise<GameOutput[]>;
  invalidateGames: () => void;

  // Leagues
  leagues: LeagueOutput[];
  leaguesLoading: boolean;
  leaguesError: string | null;
  fetchLeagues: (force?: boolean) => Promise<LeagueOutput[]>;
  invalidateLeagues: () => void;

  // Utility
  invalidateAll: () => void;
}

const DataContext = createContext<DataState | undefined>(undefined);

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Games state
  const [games, setGames] = useState<GameOutput[]>([]);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [gamesError, setGamesError] = useState<string | null>(null);
  const gamesLastFetch = useRef<number>(0);
  const gamesFetchPromise = useRef<Promise<GameOutput[]> | null>(null);

  // Leagues state
  const [leagues, setLeagues] = useState<LeagueOutput[]>([]);
  const [leaguesLoading, setLeaguesLoading] = useState(false);
  const [leaguesError, setLeaguesError] = useState<string | null>(null);
  const leaguesLastFetch = useRef<number>(0);
  const leaguesFetchPromise = useRef<Promise<LeagueOutput[]> | null>(null);

  const isCacheValid = (lastFetch: number): boolean => {
    return Date.now() - lastFetch < CACHE_DURATION;
  };

  const fetchGames = useCallback(async (force = false): Promise<GameOutput[]> => {
    // Return cached data if valid and not forced
    if (!force && games.length > 0 && isCacheValid(gamesLastFetch.current)) {
      return games;
    }

    // If already fetching, return the existing promise (deduplication)
    if (gamesFetchPromise.current) {
      return gamesFetchPromise.current;
    }

    setGamesLoading(true);
    setGamesError(null);

    const fetchPromise = DefaultService.getGamesGamesGet()
      .then((data) => {
        setGames(data);
        gamesLastFetch.current = Date.now();
        return data;
      })
      .catch((err) => {
        const errorMsg = 'Failed to load games';
        setGamesError(errorMsg);
        console.error('Error loading games:', err);
        throw err;
      })
      .finally(() => {
        setGamesLoading(false);
        gamesFetchPromise.current = null;
      });

    gamesFetchPromise.current = fetchPromise;
    return fetchPromise;
  }, [games]);

  const fetchLeagues = useCallback(async (force = false): Promise<LeagueOutput[]> => {
    // Return cached data if valid and not forced
    if (!force && leagues.length > 0 && isCacheValid(leaguesLastFetch.current)) {
      return leagues;
    }

    // If already fetching, return the existing promise (deduplication)
    if (leaguesFetchPromise.current) {
      return leaguesFetchPromise.current;
    }

    setLeaguesLoading(true);
    setLeaguesError(null);

    const fetchPromise = DefaultService.getLeaguesLeaguesGet({})
      .then((data) => {
        setLeagues(data);
        leaguesLastFetch.current = Date.now();
        return data;
      })
      .catch((err) => {
        const errorMsg = 'Failed to load leagues';
        setLeaguesError(errorMsg);
        console.error('Error loading leagues:', err);
        throw err;
      })
      .finally(() => {
        setLeaguesLoading(false);
        leaguesFetchPromise.current = null;
      });

    leaguesFetchPromise.current = fetchPromise;
    return fetchPromise;
  }, [leagues]);

  const invalidateGames = useCallback(() => {
    gamesLastFetch.current = 0;
  }, []);

  const invalidateLeagues = useCallback(() => {
    leaguesLastFetch.current = 0;
  }, []);

  const invalidateAll = useCallback(() => {
    invalidateGames();
    invalidateLeagues();
  }, [invalidateGames, invalidateLeagues]);

  return (
    <DataContext.Provider
      value={{
        games,
        gamesLoading,
        gamesError,
        fetchGames,
        invalidateGames,
        leagues,
        leaguesLoading,
        leaguesError,
        fetchLeagues,
        invalidateLeagues,
        invalidateAll,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

// Convenience hooks for specific data
export const useGames = () => {
  const { games, gamesLoading, gamesError, fetchGames, invalidateGames } = useData();
  return { games, loading: gamesLoading, error: gamesError, fetch: fetchGames, invalidate: invalidateGames };
};

export const useLeagues = () => {
  const { leagues, leaguesLoading, leaguesError, fetchLeagues, invalidateLeagues } = useData();
  return { leagues, loading: leaguesLoading, error: leaguesError, fetch: fetchLeagues, invalidate: invalidateLeagues };
};
